import { useEffect, useMemo, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { Panel } from "primereact/panel";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { DataTable, DataTablePageEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { apiService } from "../../../api/api";
import {
  AssetScraperExplorerAggregatesResponse,
  AssetScraperExplorerFilters,
  AssetScraperExplorerRow,
} from "../../../api/assetScraperExplorerApi";

interface ScrapeJob {
  id: string;
  title: string;
  brand: string;
  status: "queued" | "running" | "completed" | "failed";
  tableName?: string;
  createdAt: string;
}

const PAGE_SIZE = 50;

const ownershipOptions = [
  { label: "Any ownership type", value: undefined },
  { label: "General", value: "general" },
  { label: "Mechanical", value: "mechanical" },
  { label: "Performance", value: "performance" },
  { label: "Synchronization", value: "synchronization" },
  { label: "Synchronisation", value: "synchronisation" },
];

const assetTypeOptions = [
  { label: "Any hierarchy level", value: undefined },
  { label: "Music Video", value: "music_video" },
  { label: "Art Track", value: "art_track" },
  { label: "Sound Recording", value: "sound_recording" },
  { label: "Composition", value: "composition" },
];

const toTitleCase = (value?: string) =>
  (value || "").charAt(0).toUpperCase() + (value || "").slice(1);

const MusicAssetScraperExplorer = () => {
  const toast = useRef<Toast>(null);
  const [jobs, setJobs] = useState<ScrapeJob[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string>("");
  const [rows, setRows] = useState<AssetScraperExplorerRow[]>([]);
  const [aggregates, setAggregates] =
    useState<AssetScraperExplorerAggregatesResponse | null>(null);
  const [totalRows, setTotalRows] = useState(0);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [loadingAggregates, setLoadingAggregates] = useState(false);
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState<AssetScraperExplorerFilters>({
    assetType: undefined,
    ownerQuery: "",
    ownershipType: undefined,
    territory: "",
    q: "",
  });

  const selectedJob = useMemo(
    () => jobs.find((job) => job.id === selectedJobId),
    [jobs, selectedJobId]
  );

  const cleanedFilters = useMemo(
    () => ({
      assetType: filters.assetType || undefined,
      ownerQuery: filters.ownerQuery?.trim() || undefined,
      ownershipType: filters.ownershipType || undefined,
      territory: filters.territory?.trim() || undefined,
      q: filters.q?.trim() || undefined,
    }),
    [filters]
  );

  const loadJobs = async () => {
    setLoadingJobs(true);
    try {
      const data = (await apiService.scrapers.getScrapeJobs()) as ScrapeJob[];
      setJobs(data);
      if (!selectedJobId && data.length > 0) {
        setSelectedJobId(data[0].id);
      }
    } catch (error) {
      console.error(error);
      toast.current?.show({
        severity: "error",
        summary: "Failed to load jobs",
        detail: "Could not fetch scrape history for explorer",
      });
    } finally {
      setLoadingJobs(false);
    }
  };

  const loadExplorerRows = async (nextOffset = offset) => {
    if (!selectedJobId) return;
    setLoadingRows(true);
    try {
      const data = await apiService.scrapers.explorer.getJobExplorer(
        selectedJobId,
        {
          ...cleanedFilters,
          limit: PAGE_SIZE,
          offset: nextOffset,
        }
      );
      setRows(data.rows);
      setTotalRows(data.total);
      setOffset(nextOffset);
    } catch (error) {
      console.error(error);
      toast.current?.show({
        severity: "error",
        summary: "Failed to load explorer rows",
        detail: "Please try again or tighten filters.",
      });
    } finally {
      setLoadingRows(false);
    }
  };

  const loadAggregates = async () => {
    if (!selectedJobId) return;
    setLoadingAggregates(true);
    try {
      const data = await apiService.scrapers.explorer.getJobExplorerAggregates(
        selectedJobId,
        cleanedFilters
      );
      setAggregates(data);
    } catch (error) {
      console.error(error);
      setAggregates(null);
      toast.current?.show({
        severity: "warn",
        summary: "Aggregates unavailable",
        detail: "Rows are still available; aggregate query failed.",
      });
    } finally {
      setLoadingAggregates(false);
    }
  };

  useEffect(() => {
    loadJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedJobId) return;
    setOffset(0);
    loadExplorerRows(0);
    loadAggregates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJobId, cleanedFilters.assetType, cleanedFilters.ownerQuery, cleanedFilters.ownershipType, cleanedFilters.territory, cleanedFilters.q]);

  const onPage = (event: DataTablePageEvent) => {
    loadExplorerRows(event.first);
  };

  const ownershipRowsByType = aggregates?.ownershipRowsByType ?? [];
  const showMvSrColumns = filters.assetType !== "composition";
  const selectedOwnershipLabel = toTitleCase(filters.ownershipType);

  return (
    <div>
      <Toast ref={toast} />

      <Panel header="Music Asset Scraper Explorer">
        <div className="grid">
          <div className="col-12 md:col-4">
            <label htmlFor="job-select" className="block mb-2">
              Scrape Job
            </label>
            <Dropdown
              id="job-select"
              className="w-full"
              value={selectedJobId}
              options={jobs.map((job) => ({
                label: `${job.createdAt} • ${job.title} • ${job.status}`,
                value: job.id,
              }))}
              onChange={(e) => setSelectedJobId(e.value)}
              placeholder={loadingJobs ? "Loading jobs..." : "Select job"}
              disabled={loadingJobs}
              filter
            />
          </div>

          <div className="col-12 md:col-2">
            <label htmlFor="asset-type-filter" className="block mb-2">
              Hierarchy Level
            </label>
            <Dropdown
              id="asset-type-filter"
              className="w-full"
              value={filters.assetType}
              options={assetTypeOptions}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, assetType: e.value }))
              }
              placeholder="Any"
            />
          </div>

          <div className="col-12 md:col-2">
            <label htmlFor="ownership-filter" className="block mb-2">
              Ownership Type
            </label>
            <Dropdown
              id="ownership-filter"
              className="w-full"
              value={filters.ownershipType}
              options={ownershipOptions}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, ownershipType: e.value }))
              }
              placeholder="Any"
            />
          </div>

          <div className="col-12 md:col-2">
            <label htmlFor="owner-filter" className="block mb-2">
              Owner
            </label>
            <InputText
              id="owner-filter"
              className="w-full"
              value={filters.ownerQuery || ""}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, ownerQuery: e.target.value }))
              }
              placeholder="Display name or owner id"
            />
          </div>

          <div className="col-12 md:col-2">
            <label htmlFor="territory-filter" className="block mb-2">
              Territory
            </label>
            <InputText
              id="territory-filter"
              className="w-full"
              value={filters.territory || ""}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, territory: e.target.value }))
              }
              placeholder="US"
            />
          </div>

          <div className="col-12 md:col-8">
            <label htmlFor="search-filter" className="block mb-2">
              Search IDs / Titles
            </label>
            <InputText
              id="search-filter"
              className="w-full"
              value={filters.q || ""}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, q: e.target.value }))
              }
              placeholder="Search music video, sound recording, or composition title/id"
            />
          </div>

          <div className="col-12 md:col-4 flex align-items-end justify-content-end gap-2">
            <Button
              label="Refresh"
              icon="pi pi-refresh"
              onClick={() => {
                loadExplorerRows(offset);
                loadAggregates();
              }}
              disabled={!selectedJobId}
            />
            <Button
              label="Reset Filters"
              className="p-button-secondary"
              onClick={() =>
                setFilters({
                  assetType: undefined,
                  ownerQuery: "",
                  ownershipType: undefined,
                  territory: "",
                  q: "",
                })
              }
            />
          </div>
        </div>
      </Panel>

      <div className="grid mt-2">
        <div className="col-12 md:col-3">
          <Card title="Music Videos" subTitle={selectedJob?.title || "No job"}>
            <div className="text-2xl font-semibold">
              {loadingAggregates ? "..." : aggregates?.totals.musicVideos ?? 0}
            </div>
          </Card>
        </div>
        <div className="col-12 md:col-3">
          <Card title="With Sound Recording">
            <div className="text-2xl font-semibold">
              {loadingAggregates
                ? "..."
                : aggregates?.totals.withSoundRecording ?? 0}
            </div>
          </Card>
        </div>
        <div className="col-12 md:col-3">
          <Card title="With Composition">
            <div className="text-2xl font-semibold">
              {loadingAggregates
                ? "..."
                : aggregates?.totals.withComposition ?? 0}
            </div>
          </Card>
        </div>
        <div className="col-12 md:col-3">
          <Card title="Composition Sync (US)">
            <div className="text-2xl font-semibold">
              {loadingAggregates
                ? "..."
                : aggregates?.totals.compositionSyncUsRows ?? 0}
            </div>
          </Card>
        </div>
        <div className="col-12 md:col-3">
          <Card title="Composition Sync (Ex-US)">
            <div className="text-2xl font-semibold">
              {loadingAggregates
                ? "..."
                : aggregates?.totals.compositionSyncExUsRows ?? 0}
            </div>
          </Card>
        </div>
        <div className="col-12 md:col-6">
          <Card title="Ownership Rows by Type">
            <div style={{ fontSize: "0.9rem" }}>
              {ownershipRowsByType.length === 0 && "No rows"}
              {ownershipRowsByType.map((row) => (
                <div key={row.ownershipType}>
                  {row.ownershipType}: {row.rowCount}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <Panel header="Hierarchy Explorer Results" className="mt-3">
        <DataTable
          value={rows}
          loading={loadingRows}
          paginator
          lazy
          first={offset}
          rows={PAGE_SIZE}
          totalRecords={totalRows}
          onPage={onPage}
          responsiveLayout="scroll"
          emptyMessage="No rows found for this job and filter set"
        >
          {showMvSrColumns && (
            <Column field="musicVideoAssetId" header="Music Video Asset ID" />
          )}
          {showMvSrColumns && (
            <Column field="musicVideoAssetTitle" header="Music Video Title" />
          )}
          {showMvSrColumns && (
            <Column field="soundRecordingAssetId" header="Sound Recording ID" />
          )}
          {showMvSrColumns && (
            <Column
              field="soundRecordingAssetTitle"
              header="Sound Recording Title"
            />
          )}
          <Column field="compositionAssetId" header="Composition Asset ID" />
          <Column field="compositionAssetTitle" header="Composition Title" />
          <Column
            field="compositionWriters"
            header="Composition Writers"
            style={{ minWidth: "16rem" }}
          />
          {filters.ownershipType ? (
            <>
              {showMvSrColumns && (
                <Column
                  field="musicVideoOwnershipSelected"
                  header={`MV ${selectedOwnershipLabel} Ownership`}
                  style={{ minWidth: "18rem" }}
                />
              )}
              {showMvSrColumns && (
                <Column
                  field="soundRecordingOwnershipSelected"
                  header={`SR ${selectedOwnershipLabel} Ownership`}
                  style={{ minWidth: "18rem" }}
                />
              )}
              <Column
                field="compositionOwnershipSelected"
                header={`Composition ${selectedOwnershipLabel} Ownership`}
                style={{ minWidth: "20rem" }}
              />
            </>
          ) : (
            <>
              {showMvSrColumns && (
                <Column
                  field="musicVideoOwnersGlobal"
                  header="MV General Ownership"
                  style={{ minWidth: "18rem" }}
                />
              )}
              {showMvSrColumns && (
                <Column
                  field="soundRecordingOwnersGlobal"
                  header="SR General Ownership"
                  style={{ minWidth: "18rem" }}
                />
              )}
              <Column
                field="compositionSyncUs"
                header="Composition Sync US"
                style={{ minWidth: "14rem" }}
              />
              <Column
                field="compositionSyncExUs"
                header="Composition Sync Ex-US"
                style={{ minWidth: "18rem" }}
              />
            </>
          )}
        </DataTable>
      </Panel>
    </div>
  );
};

export default MusicAssetScraperExplorer;

